<template>
  <header class="monitor-header">
    <div class="brand-search">
      <div class="brand">
        <div class="brand-dot"></div>

        <div class="brand-text">
          <div class="brand-title">Sensoreal Master Monitor</div>
          <div class="brand-subtitle">Live monitoring dashboard</div>
        </div>
      </div>

      <div class="search-actions">
        <div class="search-wrap">
          <input
            :value="searchQuery"
            type="text"
            class="monitor-search"
            placeholder="Search devices, servers etc."
            @input="$emit('update:searchQuery', $event.target.value)"
          />

          <button
            v-if="searchQuery"
            class="search-clear"
            type="button"
            @click="$emit('update:searchQuery', '')"
          >
            ×
          </button>
        </div>

        <button class="log-issue-button" type="button" @click="openSnitchLog">
          <span class="log-issue-button__emoji">🪵</span>
          <span>Log Issue</span>
        </button>
      </div>
    </div>

    <div class="global-alerts">
      <template v-if="hasAnyAlerts">
        <div v-if="alerts.apisDown > 0" class="alert-chip critical">
          APIS DOWN {{ alerts.apisDown }}
        </div>

        <div v-if="alerts.devicesOffline > 0" class="alert-chip critical">
          DEVICES OFFLINE {{ alerts.devicesOffline }}
        </div>

        <!-- <div v-if="alerts.screensMissing > 0" class="alert-chip warning">
          SCREENS MISSING {{ alerts.screensMissing }}
        </div> -->

        <div v-if="alerts.highDisk > 0" class="alert-chip warning">
          HIGH DISK {{ alerts.highDisk }}
        </div>
      </template>

      <template v-else>
        <div class="alert-chip healthy">ALL SYSTEMS HEALTHY</div>
      </template>
    </div>

    <nav class="nav-links">
      <router-link to="/" exact-active-class="is-active">Overview</router-link>
      <router-link to="/snitches" active-class="is-active">Snitch</router-link>
      <router-link to="/screen-snitch" active-class="is-active"
        >Screens</router-link
      >
      <a href="/security" target="_blank">Server</a>
      <a href="/nexus" target="_blank">Nexus</a>
      <a href="https://kuma.sensoreal.net" target="_blank" rel="noopener">
        Kuma
      </a>
    </nav>
  </header>
</template>

<script>
import { getMonitorAlerts } from "@/store/monitorAlerts";

export default {
  props: {
    searchQuery: {
      type: String,
      default: "",
    },
  },

  emits: ["update:searchQuery"],

  computed: {
    alerts() {
      return getMonitorAlerts();
    },

    hasAnyAlerts() {
      return (
        this.alerts.devicesOffline > 0 ||
        this.alerts.apisDown > 0 ||
        this.alerts.highDisk > 0
      );
    },
  },

  methods: {
    openSnitchLog() {
      window.open(
        "notion://www.notion.so/martinsbrandhouse/3340783528788031b260f9702194e0c3?v=3340783528788057a600000c6c2e4a99&source=copy_link",
        "_blank",
        "noopener"
      );
    },
  },
};
</script>

<style scoped>
.monitor-header {
  flex-shrink: 0;
  display: grid;
  grid-template-columns: minmax(0, auto) 1fr auto;
  align-items: center;
  gap: 16px;
  min-height: 72px;
  padding: 0 16px;
  background: #08131f;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
}

.brand-search {
  display: flex;
  align-items: center;
  gap: 18px;
  min-width: 0;
}

.brand {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  flex-shrink: 0;
}

.brand-dot {
  width: 18px;
  height: 18px;
  border-radius: 999px;
  background: #16c79a;
  box-shadow: 0 0 16px rgba(22, 199, 154, 0.3);
  flex-shrink: 0;
}

.brand-text {
  min-width: 0;
}

.brand-title {
  color: #f1f5f9;
  font-size: 1.15rem;
  font-weight: 700;
  line-height: 1.2;
}

.brand-subtitle {
  margin-top: 2px;
  color: #8fa0b2;
  font-size: 0.78rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.search-wrap {
  position: relative;
  width: min(420px, 100%);
  min-width: 220px;
}

.monitor-search {
  width: 100%;
  height: 40px;
  padding: 0 40px 0 14px;
  border-radius: 999px;
  border: 1px solid rgba(143, 160, 178, 0.22);
  background: rgba(255, 255, 255, 0.04);
  color: #f1f5f9;
  font-size: 0.92rem;
  outline: none;
  transition: border-color 0.2s ease, background 0.2s ease;
}

.monitor-search::placeholder {
  color: #8fa0b2;
}

.monitor-search:focus {
  border-color: rgba(22, 199, 154, 0.45);
  background: rgba(255, 255, 255, 0.06);
}

.search-clear {
  position: absolute;
  top: 50%;
  right: 10px;
  transform: translateY(-50%);
  width: 24px;
  height: 24px;
  border: 0;
  border-radius: 999px;
  background: transparent;
  color: #8fa0b2;
  cursor: pointer;
  font-size: 1rem;
  line-height: 1;
}

.search-clear:hover {
  background: rgba(255, 255, 255, 0.08);
  color: #f1f5f9;
}

.global-alerts {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  min-width: 0;
  flex-wrap: wrap;
}

.alert-chip {
  padding: 7px 12px;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  line-height: 1;
  white-space: nowrap;
  border: 1px solid transparent;
}

.alert-chip.critical {
  background: rgba(239, 68, 68, 0.16);
  color: #f87171;
  border-color: rgba(239, 68, 68, 0.35);
}

.alert-chip.warning {
  background: rgba(245, 158, 11, 0.16);
  color: #fbbf24;
  border-color: rgba(245, 158, 11, 0.35);
}

.alert-chip.neutral {
  background: rgba(56, 189, 248, 0.12);
  color: #7dd3fc;
  border-color: rgba(56, 189, 248, 0.28);
}

.alert-chip.healthy {
  background: rgba(34, 197, 94, 0.14);
  color: #86efac;
  border-color: rgba(34, 197, 94, 0.28);
}

.nav-links {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
  flex-wrap: wrap;
}

.nav-links a {
  color: #cbd5e1;
  text-decoration: none;
  font-size: 0.92rem;
  padding: 9px 14px;
  border-radius: 999px;
  transition: background 0.2s ease;
}

.nav-links a:hover {
  background: rgba(255, 255, 255, 0.05);
}

.nav-links a.is-active {
  background: rgba(22, 199, 154, 0.12);
  color: #ecfdf5;
}

.search-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  width: min(520px, 100%);
  min-width: 260px;
}

.search-wrap {
  flex: 1;
  position: relative;
}

.log-issue-button {
  flex-shrink: 0;
}

.log-issue-button {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 40px;
  padding: 0 14px;
  border-radius: 999px;
  border: 1px solid rgba(143, 160, 178, 0.22);
  background: rgba(255, 255, 255, 0.04);
  color: #e7edf3;
  font-size: 0.88rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.log-issue-button:hover {
  background: rgba(255, 255, 255, 0.08);
  border-color: rgba(143, 160, 178, 0.4);
  transform: translateY(-1px);
}

.log-issue-button__icon {
  width: 16px;
  height: 16px;
  opacity: 0.9;
}

.log-issue-button__emoji {
  font-size: 16px;
  line-height: 1;
  display: flex;
  align-items: center;
}

.log-issue-button:hover .log-issue-button__emoji {
  transform: rotate(-10deg);
  transition: transform 0.2s ease;
}

@media (max-width: 1600px) {
  .monitor-header {
    grid-template-columns: 1fr;
    align-items: flex-start;
    padding: 12px 16px;
  }

  .brand-search {
    width: 100%;
    flex-wrap: wrap;
  }

  .search-wrap {
    width: 100%;
    max-width: 420px;
  }

  .global-alerts {
    justify-content: flex-start;
  }

  .nav-links {
    width: 100%;
    justify-content: flex-start;
  }
}

@media (max-width: 700px) {
  .brand-search {
    gap: 12px;
  }

  .search-wrap {
    min-width: 0;
    max-width: none;
  }
}
</style>
